import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Settings, Key, Eye, EyeOff, Save, CheckCircle, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const OSINT_BACKEND = process.env.REACT_APP_BACKEND_URL;

const apiServices = [
  {
    id: 'virustotal',
    name: 'VirusTotal',
    description: 'File, URL, domain, and IP analysis',
    url: 'https://www.virustotal.com/gui/my-apikey',
    category: 'Threat Intelligence'
  },
  {
    id: 'abuseipdb',
    name: 'AbuseIPDB',
    description: 'IP address reputation and abuse reports',
    url: 'https://www.abuseipdb.com/account/api',
    category: 'IP Intelligence'
  },
  {
    id: 'shodan',
    name: 'Shodan',
    description: 'Internet-wide scanning and IoT device search',
    url: 'https://account.shodan.io/',
    category: 'Infrastructure'
  },
  {
    id: 'ipquery',
    name: 'IPQuery',
    description: 'Lightweight IP geolocation and ASN info',
    url: 'https://ipquery.io',
    category: 'IP Intelligence',
    noKey: true
  },
  {
    id: 'ipapi',
    name: 'ipapi',
    description: 'IP geolocation and timezone data',
    url: 'https://ipapi.co',
    category: 'IP Intelligence',
    noKey: true
  },
  {
    id: 'ipdata',
    name: 'ipdata',
    description: 'Threat intel and proxy detection',
    url: 'https://ipdata.co',
    category: 'IP Intelligence'
  },
  {
    id: 'securitytrails',
    name: 'SecurityTrails',
    description: 'Subdomain and DNS history',
    url: 'https://securitytrails.com/app/account',
    category: 'Domain Intelligence'
  },
  {
    id: 'urlscan',
    name: 'URLScan.io',
    description: 'Website screenshots and DOM analysis',
    url: 'https://urlscan.io/user/profile/',
    category: 'Domain Intelligence',
    noKey: true
  },
  {
    id: 'hibp',
    name: 'Have I Been Pwned',
    description: 'Data breach history lookup',
    url: 'https://haveibeenpwned.com/API/Key',
    category: 'Identity'
  },
  {
    id: 'hunterio',
    name: 'Hunter.io',
    description: 'Email finding and verification',
    url: 'https://hunter.io/api-keys',
    category: 'Identity'
  },
  {
    id: 'censys',
    name: 'Censys',
    description: 'Host discovery and SSL certificate data',
    url: 'https://search.censys.io/account/api',
    category: 'Infrastructure'
  },
  {
    id: 'binaryedge',
    name: 'BinaryEdge',
    description: 'Real-time global internet scanning',
    url: 'https://app.binaryedge.io/account/api',
    category: 'Infrastructure'
  },
  {
    id: 'alienvault',
    name: 'AlienVault OTX',
    description: 'Open threat intelligence community',
    url: 'https://otx.alienvault.com/api',
    category: 'Threat Intelligence'
  },
  {
    id: 'malwarebazaar',
    name: 'MalwareBazaar',
    description: 'Malware sample repository',
    url: 'https://bazaar.abuse.ch/api/',
    category: 'Threat Intelligence',
    noKey: true
  },
  {
    id: 'threatfox',
    name: 'ThreatFox',
    description: 'IOC sharing platform',
    url: 'https://threatfox.abuse.ch/api/',
    category: 'Threat Intelligence',
    noKey: true
  }
];

const APIKeySettings = () => {
  const [apiKeys, setApiKeys] = useState({});
  const [enabledServices, setEnabledServices] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${OSINT_BACKEND}/api/osint/settings/apikeys/`);
      if (response.data) {
        const keys = {};
        const enabled = {};
        response.data.forEach(item => {
          keys[item.service] = item.api_key || '';
          enabled[item.service] = item.enabled || false;
        });
        setApiKeys(keys);
        setEnabledServices(enabled);
      }
    } catch (error) {
      console.error('Error fetching API key settings:', error);
      // Initialize with empty values
      const keys = {};
      const enabled = {};
      apiServices.forEach(service => {
        keys[service.id] = '';
        enabled[service.id] = service.noKey || false;
      });
      setApiKeys(keys);
      setEnabledServices(enabled);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyChange = (serviceId, value) => {
    setApiKeys(prev => ({ ...prev, [serviceId]: value }));
  };

  const handleToggleService = (serviceId) => {
    setEnabledServices(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));
  };

  const toggleShowKey = (serviceId) => {
    setShowKeys(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const settings = apiServices.map(service => ({
        service: service.id,
        api_key: apiKeys[service.id] || '',
        enabled: enabledServices[service.id] || false
      }));

      await axios.post(`${OSINT_BACKEND}/api/osint/settings/apikeys/`, settings);
      toast.success('API key settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const groupedServices = apiServices.reduce((acc, service) => {
    if (!acc[service.category]) acc[service.category] = [];
    acc[service.category].push(service);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold font-heading mb-2" data-testid="api-settings-title">
            API Key Settings
          </h2>
          <p className="text-muted-foreground">
            Configure API keys to enable additional OSINT services
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Getting API Keys</p>
              <p>
                Click the external link next to each service to sign up and get your API key. 
                Services marked as &quot;No Key Required&quot; work without authentication.
                Your API keys are stored securely and used only for OSINT queries.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Services by Category */}
      {Object.entries(groupedServices).map(([category, services]) => (
        <Card key={category} className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              {category}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {services.map(service => (
              <div 
                key={service.id} 
                className={`p-4 rounded-lg border transition-all ${
                  enabledServices[service.id] ? 'bg-green-50/50 border-green-200' : 'bg-muted/30'
                }`}
                data-testid={`service-${service.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{service.name}</h4>
                      <a
                        href={service.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      {service.noKey && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                          No Key Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{service.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {enabledServices[service.id] && (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    )}
                    <Switch
                      checked={enabledServices[service.id] || false}
                      onCheckedChange={() => handleToggleService(service.id)}
                      data-testid={`toggle-${service.id}`}
                    />
                  </div>
                </div>
                
                {!service.noKey && (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showKeys[service.id] ? 'text' : 'password'}
                        value={apiKeys[service.id] || ''}
                        onChange={(e) => handleKeyChange(service.id, e.target.value)}
                        placeholder={`Enter ${service.name} API key`}
                        className="pr-10 font-mono text-sm"
                        data-testid={`apikey-${service.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey(service.id)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showKeys[service.id] ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Usage Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Service Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {Object.values(enabledServices).filter(Boolean).length}
              </div>
              <div className="text-xs text-muted-foreground">Services Enabled</div>
            </div>
            <div className="p-4 bg-muted rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {Object.values(apiKeys).filter(k => k && k.length > 0).length}
              </div>
              <div className="text-xs text-muted-foreground">API Keys Configured</div>
            </div>
            <div className="p-4 bg-muted rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {apiServices.filter(s => s.noKey).length}
              </div>
              <div className="text-xs text-muted-foreground">Free Services</div>
            </div>
            <div className="p-4 bg-muted rounded-lg text-center">
              <div className="text-2xl font-bold">
                {apiServices.length}
              </div>
              <div className="text-xs text-muted-foreground">Total Services</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default APIKeySettings;
